import { useEffect, useRef, useState } from "react";
import { buildFramesZip, framesZipFilename } from "../export/framesZip";
import { useOnline } from "../offline/useOnline";
import type { Frame, POI } from "../api/types";

export interface ExportFramesButtonProps {
  flightId: string;
  /** The frames the list is showing right now (the active filter applied). */
  frames: Frame[];
  pois: POI[];
  /** The active filter's name; it is part of the label ("Export mine (4)"). */
  filterLabel: string;
  tz?: string;
  /** Resolve a fresh presigned GET URL for a frame's full-res JPEG. */
  frameUrl: (frameId: string) => Promise<string>;
  /** "Include all POIs": present when the flight has POIs with no saved frame. */
  capture?: {
    missingCount: number;
    /** Saves the frames, then resolves with the list to export afterwards. */
    run: (o: {
      signal: AbortSignal;
      onProgress: (done: number, total: number) => void;
    }) => Promise<{ failed: number; frames: Frame[]; pois: POI[] }>;
  };
}

type Phase =
  | { kind: "idle" }
  | { kind: "confirm" }
  | { kind: "capturing"; done: number; total: number }
  | { kind: "exporting"; done: number; total: number };

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** "Export all": zips the full-res JPEG of every listed frame and saves it,
 *  optionally saving a frame for every POI that has none first. */
export function ExportFramesButton({
  flightId, frames, pois, filterLabel, tz, frameUrl, capture,
}: ExportFramesButtonProps): JSX.Element {
  const online = useOnline();
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [note, setNote] = useState<string | null>(null);
  const [includePois, setIncludePois] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Set in the unmount cleanup: a capture or export in flight when the
  // reviewer leaves the page must not go on to build/download a ZIP, or
  // touch state on a component that is no longer mounted.
  const unmountedRef = useRef(false);
  const missing = capture?.missingCount ?? 0;
  const busy = phase.kind === "capturing" || phase.kind === "exporting";

  // The message describes the last export of one filter's frames; under a
  // different filter it would read as being about the wrong set.
  useEffect(() => { setNote(null); }, [filterLabel]);
  // Leaving the page mid-capture stops it after the POI in hand. React 18
  // StrictMode (dev only) mounts, runs this cleanup, then remounts — reset
  // the flag on (re)mount, or a StrictMode dev session would see the very
  // first mount's cleanup set it permanently, breaking every export.
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; abortRef.current?.abort(); };
  }, []);
  // The reason the confirm line is open (a nonzero missing count) can vanish
  // out from under it — the filter changes, or another reviewer frames the
  // last POI and the list refreshes — in which case it must not go on
  // describing (or saving frames for) zero POIs.
  useEffect(() => {
    if (phase.kind === "confirm" && missing === 0) setPhase({ kind: "idle" });
  }, [phase.kind, missing]);

  const exportFrames = async (list: Frame[], listPois: POI[], notes: string[]): Promise<void> => {
    setPhase({ kind: "exporting", done: 0, total: list.length });
    const { blob, failures } = await buildFramesZip({
      frames: list, pois: listPois, tz, frameUrl,
      onProgress: (done, total) => {
        if (!unmountedRef.current) setPhase({ kind: "exporting", done, total });
      },
    });
    // A plain export that is mid-download when the reviewer leaves must not
    // pop a download onto whatever page they're on now.
    if (unmountedRef.current) return;
    if (blob === null) {
      notes.push("Couldn't download any frames.");
    } else {
      saveBlob(blob, framesZipFilename(flightId));
      if (failures > 0) notes.push(`${count(failures, "frame", "frames")} couldn't be downloaded.`);
    }
  };

  const handleExport = async (withCapture: boolean): Promise<void> => {
    setNote(null);
    const notes: string[] = [];
    try {
      let list = frames;
      let listPois = pois;
      let skipExport = false;
      if (withCapture && capture !== undefined) {
        const ctl = new AbortController();
        abortRef.current = ctl;
        setPhase({ kind: "capturing", done: 0, total: missing });
        let res: Awaited<ReturnType<typeof capture.run>>;
        try {
          res = await capture.run({
            signal: ctl.signal,
            onProgress: (done, total) => {
              if (!unmountedRef.current) setPhase({ kind: "capturing", done, total });
            },
          });
        } catch (err) {
          if (!unmountedRef.current) {
            setNote(`Couldn't save POI frames: ${(err as Error).message}`);
          }
          return;
        }
        // capture.run resolves (rather than rejecting) when it's stopped by
        // the unmount abort; carrying on from here would fetch every frame
        // and download a ZIP onto whatever page the reviewer is on now.
        if (unmountedRef.current) return;
        if (res.failed > 0) notes.push(`${count(res.failed, "POI", "POIs")} couldn't be captured.`);
        list = res.frames;
        listPois = res.pois;
        // Nothing to export is not a download failure — there was nothing
        // to download.
        skipExport = list.length === 0;
      }
      if (!skipExport) await exportFrames(list, listPois, notes);
    } catch (err) {
      notes.push(`Export failed: ${(err as Error).message}`);
    } finally {
      if (!unmountedRef.current) {
        if (notes.length > 0) setNote(notes.join(" "));
        abortRef.current = null;
        setIncludePois(false);
        setPhase({ kind: "idle" });
      }
    }
  };

  const onExportClick = (): void => {
    if (includePois && missing > 0) setPhase({ kind: "confirm" });
    else void handleExport(false);
  };

  const label =
    phase.kind === "capturing" ? `Saving POI frames ${phase.done} of ${phase.total}…`
      : phase.kind === "exporting" ? `Exporting ${phase.done} of ${phase.total}…`
        : `Export ${filterLabel} (${frames.length})`;

  return (
    <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      {note !== null && <span role="status" style={{ fontSize: 13 }}>{note}</span>}
      {phase.kind === "confirm" ? (
        <>
          <span style={{ fontSize: 13 }}>
            {missing === 1
              ? "This saves a frame for 1 POI and adds it to Saved Frames for everyone on the team."
              : `This saves a frame for ${missing} POIs and adds them to Saved Frames for everyone on the team.`}
          </span>
          <button
            type="button"
            onClick={() => { void handleExport(true); }}
            disabled={!online}
            title={online ? undefined : "You're offline"}
            style={{ padding: "4px 10px" }}
          >
            Save and export
          </button>
          <button type="button" onClick={() => setPhase({ kind: "idle" })} style={{ padding: "4px 10px" }}>
            Cancel
          </button>
        </>
      ) : (
        <>
          {missing > 0 && (
            <label style={{ display: "inline-flex", gap: 4, alignItems: "center", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={includePois}
                disabled={busy || !online}
                onChange={(e) => setIncludePois(e.target.checked)}
              />
              {`Include all POIs (${missing} without a frame)`}
            </label>
          )}
          <button
            type="button"
            onClick={onExportClick}
            disabled={busy || !online || (frames.length === 0 && !(includePois && missing > 0))}
            title={online ? undefined : "You're offline"}
            style={{ padding: "4px 10px" }}
          >
            {label}
          </button>
          {phase.kind === "capturing" && (
            <button
              type="button"
              onClick={() => abortRef.current?.abort()}
              style={{
                padding: 0, border: "none", background: "none", color: "inherit",
                textDecoration: "underline", cursor: "pointer", fontSize: 13,
              }}
            >
              Stop
            </button>
          )}
        </>
      )}
    </span>
  );
}
